import React, { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { AuthProvider } from './hooks/useAuth'
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import Home from './pages/Home'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import PDFEditor from './pages/PDFEditor'
import VisorDocumento from './pages/VisorDocumento'
import SignaturesHistory from './pages/SignaturesHistory'
import HistorialGeneral from './pages/HistorialGeneral'
import Configuration from './pages/Configuration'
import EmailConfig from './pages/EmailConfig'
import DatabaseConfig from './pages/DatabaseConfig'
import PDFVisualConfig from './pages/PDFVisualConfig'
import UserManagement from './pages/UserManagement'
import DocumentosPendientes from './pages/DocumentosPendientes'
import FirmaExitosa from './pages/FirmaExitosa'
import RegistrarFirmante from './pages/RegistrarFirmante'
import ChangePassword from './pages/ChangePassword'
import ResetPassword from './pages/ResetPassword'
import SignatureConfig from './pages/SignatureConfig'
import BulkDownload from './pages/BulkDownload'
import GlobalShareModal from './components/GlobalShareModal'
import TourManager from './components/TourManager'
import ReleaseAnnouncement from './components/ReleaseAnnouncement'
import ItestEnvironmentBanner from './components/ItestEnvironmentBanner'
import './App.css'

// Componente para redirigir a login si no está autenticado
const RedirectToLogin = () => {
  return <Navigate to="/login" replace />
}

// Componente para interceptar navegaciones de React Router
const NavigationBlocker = ({ children }) => {
  const location = useLocation()
  const navigate = useNavigate()
  
  React.useEffect(() => {
    const savedShowModal = sessionStorage.getItem('pdfEditor_showShareModal')
    if (savedShowModal === 'true') {
      console.log('[NavigationBlocker] 🚫 Modal abierto, bloqueando navegaciones de React Router')
      
      // Interceptar navigate
      const originalNavigate = navigate
      const blockedNavigate = (to, options) => {
        const currentModal = sessionStorage.getItem('pdfEditor_showShareModal')
        if (currentModal === 'true') {
          console.log('[NavigationBlocker] 🚫 BLOQUEANDO navigate a:', to)
          return
        }
        return originalNavigate(to, options)
      }
      
      // Bloquear cambios de ruta
      const unblock = () => {
        const currentModal = sessionStorage.getItem('pdfEditor_showShareModal')
        if (currentModal === 'true') {
          console.log('[NavigationBlocker] 🚫 BLOQUEANDO cambio de ruta')
          return false
        }
        return true
      }
      
      return () => {
        // Cleanup
      }
    }
  }, [location.pathname, navigate])
  
  return children
}

function App() {
  // Interceptar navegaciones cuando el modal está abierto
  React.useEffect(() => {
    const checkModalAndBlockNavigation = () => {
      const savedShowModal = sessionStorage.getItem('pdfEditor_showShareModal')
      if (savedShowModal === 'true') {
        console.log('[App] 🚫 BLOQUEANDO navegación - modal de compartir está abierto en sessionStorage')
        // Prevenir navegación usando beforeunload
        const preventNav = (e) => {
          e.preventDefault()
          e.returnValue = ''
        }
        window.addEventListener('beforeunload', preventNav)
        return () => window.removeEventListener('beforeunload', preventNav)
      }
    }
    checkModalAndBlockNavigation()
  }, [])

  return (
    <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
      <AuthProvider>
        <NavigationBlocker>
        <div className="min-h-screen bg-gray-50">
          <ItestEnvironmentBanner />
          <Routes>
            {/* Ruta raíz - redirige a login */}
            <Route path="/" element={<RedirectToLogin />} />
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/change-password" element={
              <ProtectedRoute>
                <ChangePassword />
              </ProtectedRoute>
            } />
            
            {/* Rutas protegidas con Layout (Sidebar) */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/editor" element={
              <ProtectedRoute>
                <Layout>
                  <PDFEditor />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/editor/:token" element={
              <ProtectedRoute>
                <Layout>
                  <PDFEditor />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/ver-documento/:id" element={
              <ProtectedRoute>
                <Layout>
                  <VisorDocumento />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/firmados-pendientes" element={
              <ProtectedRoute>
                <Layout>
                  <SignaturesHistory />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/historial-general" element={
              <ProtectedRoute>
                <Layout>
                  <HistorialGeneral />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/config" element={
              <ProtectedRoute>
                <Layout>
                  <Configuration />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/config/email" element={
              <ProtectedRoute>
                <Layout>
                  <EmailConfig />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/config/database" element={
              <ProtectedRoute>
                <Layout>
                  <DatabaseConfig />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/config/signature" element={
              <ProtectedRoute>
                <Layout>
                  <SignatureConfig />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/config/pdf-visual" element={
              <ProtectedRoute>
                <Layout>
                  <PDFVisualConfig />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/config/bulk-download" element={
              <ProtectedRoute>
                <Layout>
                  <BulkDownload />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/users" element={
              <ProtectedRoute>
                <Layout>
                  <UserManagement />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/documentos-pendientes" element={
              <ProtectedRoute>
                <Layout>
                  <DocumentosPendientes />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/registrar/:token" element={<RegistrarFirmante />} />
            <Route path="/firma-exitosa" element={<FirmaExitosa />} />
          </Routes>
          
          <Toaster 
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
            }}
          />
          
          <ReleaseAnnouncement />
          <TourManager />
          {/* Modal global que persiste incluso si PDFEditor se desmonta */}
          <GlobalShareModal />
        </div>
        </NavigationBlocker>
      </AuthProvider>
    </Router>
  )
}

export default App
